import { EventBus } from '../state/EventBus';
import { Transaction, Block } from '../models';

export interface MempoolWSMessage {
  action: 'init' | 'want' | 'response';
  data?: string[] | any;
}

export interface BlockchainWSMessage {
  op: 'unconfirmed_sub' | 'blocks_sub' | 'ping' | 'utx' | 'block';
  x?: any;
}

export interface ConnectionManager {
  reconnectDelay: number;
  maxReconnectAttempts: number;
  heartbeatInterval: number;
}

export interface WebSocketConfig {
  mempool: {
    wsUrl: string;
    subscriptions: string[];
  };
  blockchain: {
    wsUrl: string;
  };
  testMode?: boolean;
  connectionManager: ConnectionManager;
}

export class WebSocketManager {
  private mempoolWS: WebSocket | null = null;
  private blockchainWS: WebSocket | null = null;
  private eventBus: EventBus;
  private config: WebSocketConfig;
  private reconnectAttempts: Map<string, number> = new Map();
  private heartbeatIntervals: Map<string, NodeJS.Timeout> = new Map();

  constructor(config: WebSocketConfig, eventBus?: EventBus) {
    this.config = config;
    this.eventBus = eventBus || new EventBus();
  }

  async connectWebSockets(): Promise<void> {
    if (this.config.testMode) {
      this.setupTestMode();
      return;
    }

    await Promise.all([
      this.connectMempool(),
      this.connectBlockchain()
    ]);
  }

  private async connectMempool(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.mempoolWS = new WebSocket(this.config.mempool.wsUrl);

        this.mempoolWS.onopen = () => {
          console.log('Mempool WebSocket connected');
          this.resetReconnectAttempts('mempool');
          this.setupHeartbeat('mempool');
          this.subscribeMempoolChannels();
          this.emitConnectionStatus('mempool', 'connected');
          resolve();
        };

        this.mempoolWS.onmessage = (event) => {
          this.handleMempoolMessage(event.data);
        };

        this.mempoolWS.onclose = () => {
          console.log('Mempool WebSocket disconnected');
          this.emitConnectionStatus('mempool', 'disconnected');
          this.scheduleReconnect('mempool');
        };

        this.mempoolWS.onerror = (error) => {
          console.error('Mempool WebSocket error:', error);
          this.emitConnectionStatus('mempool', 'error', 'Connection error');
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  private async connectBlockchain(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.blockchainWS = new WebSocket(this.config.blockchain.wsUrl);

        this.blockchainWS.onopen = () => {
          console.log('Blockchain WebSocket connected');
          this.resetReconnectAttempts('blockchain');
          this.setupHeartbeat('blockchain');
          this.subscribeBlockchainChannels();
          this.emitConnectionStatus('blockchain', 'connected');
          resolve();
        };

        this.blockchainWS.onmessage = (event) => {
          this.handleBlockchainMessage(event.data);
        };

        this.blockchainWS.onclose = () => {
          console.log('Blockchain WebSocket disconnected');
          this.emitConnectionStatus('blockchain', 'disconnected');
          this.scheduleReconnect('blockchain');
        };

        this.blockchainWS.onerror = (error) => {
          console.error('Blockchain WebSocket error:', error);
          this.emitConnectionStatus('blockchain', 'error', 'Connection error');
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  private subscribeMempoolChannels(): void {
    if (!this.mempoolWS || this.mempoolWS.readyState !== WebSocket.OPEN) return;

    const subscribeMessage: MempoolWSMessage = {
      action: 'want',
      data: this.config.mempool.subscriptions
    };

    this.mempoolWS.send(JSON.stringify(subscribeMessage));
  }

  private subscribeBlockchainChannels(): void {
    if (!this.blockchainWS || this.blockchainWS.readyState !== WebSocket.OPEN) return;

    this.blockchainWS.send(JSON.stringify({ op: 'unconfirmed_sub' }));
    this.blockchainWS.send(JSON.stringify({ op: 'blocks_sub' }));
  }

  private handleMempoolMessage(data: string): void {
    try {
      const message: MempoolWSMessage = JSON.parse(data);
      
      if (message.action === 'response') {
        // Handle different response types based on subscription
        this.processMempoolData(message.data);
      }
    } catch (error) {
      console.error('Error parsing Mempool message:', error);
    }
  }

  private handleBlockchainMessage(data: string): void {
    try {
      const message: BlockchainWSMessage = JSON.parse(data);
      
      switch (message.op) {
        case 'utx':
          this.processTransaction(message.x);
          break;
        case 'block':
          this.processBlock(message.x);
          break;
        case 'ping':
          // Respond to ping
          if (this.blockchainWS?.readyState === WebSocket.OPEN) {
            this.blockchainWS.send(JSON.stringify({ op: 'ping' }));
          }
          break;
      }
    } catch (error) {
      console.error('Error parsing Blockchain message:', error);
    }
  }

  private processMempoolData(_data: any): void {
    // Process mempool-specific data (blocks, stats, etc.)
    // This will be expanded based on specific mempool.space WebSocket format
  }

  private processTransaction(txData: any): void {
    try {
      const transaction: Transaction = this.parseTransaction(txData);
      this.eventBus.emit({
        type: 'transaction',
        timestamp: Date.now(),
        data: transaction
      });
    } catch (error) {
      console.error('Error processing transaction:', error);
    }
  }

  private processBlock(blockData: any): void {
    try {
      const block: Block = this.parseBlock(blockData);
      this.eventBus.emit({
        type: 'block',
        timestamp: Date.now(),
        data: block
      });
    } catch (error) {
      console.error('Error processing block:', error);
    }
  }

  private parseTransaction(data: any): Transaction {
    const validateNumber = (value: any, fallback: number = 0): number => {
      const parsed = Number(value);
      return isNaN(parsed) ? fallback : parsed;
    };

    const fee = validateNumber(data.fee, 0);
    const size = validateNumber(data.size, 0);

    return {
      id: data.hash || '',
      amount: data.out?.reduce((sum: number, output: any) => sum + validateNumber(output.value, 0), 0) || 0,
      fee: fee,
      feeRate: fee > 0 && size > 0 ? Math.round(fee / size) : 0,
      size: size,
      timestamp: data.time ? validateNumber(data.time, 0) * 1000 : Date.now(),
      inputs: data.inputs?.map((input: any) => ({
        txid: input.prev_out?.hash || '',
        vout: validateNumber(input.prev_out?.n, 0),
        scriptSig: input.script || '',
        sequence: validateNumber(input.sequence, 0)
      })) || [],
      outputs: data.out?.map((output: any) => ({
        value: validateNumber(output.value, 0),
        scriptPubKey: output.script || '',
        address: output.addr
      })) || [],
      status: 'pending'
    };
  }

  private parseBlock(data: any): Block {
    return {
      height: data.height || 0,
      hash: data.hash || '',
      timestamp: data.time ? data.time * 1000 : Date.now(),
      transactionCount: data.n_tx || 0,
      size: data.size || 0,
      weight: data.weight || 0,
      totalFees: data.fee || 0,
      subsidyReward: data.reward || 0,
      difficulty: data.bits || 0,
      nonce: data.nonce || 0,
      merkleRoot: data.mrkl_root || '',
      previousBlockHash: data.prev_block || ''
    };
  }

  private setupHeartbeat(source: string): void {
    const interval = setInterval(() => {
      const ws = source === 'mempool' ? this.mempoolWS : this.blockchainWS;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ op: 'ping' }));
      }
    }, this.config.connectionManager.heartbeatInterval);

    this.heartbeatIntervals.set(source, interval);
  }

  private scheduleReconnect(source: string): void {
    const attempts = this.reconnectAttempts.get(source) || 0;
    
    if (attempts >= this.config.connectionManager.maxReconnectAttempts) {
      console.error(`Max reconnection attempts reached for ${source}`);
      return;
    }

    this.reconnectAttempts.set(source, attempts + 1);
    
    const delay = Math.min(
      this.config.connectionManager.reconnectDelay * Math.pow(2, attempts),
      30000 // Max 30 seconds
    );

    setTimeout(() => {
      console.log(`Attempting to reconnect ${source} (attempt ${attempts + 1})`);
      if (source === 'mempool') {
        this.connectMempool().catch(console.error);
      } else {
        this.connectBlockchain().catch(console.error);
      }
    }, delay);
  }

  private resetReconnectAttempts(source: string): void {
    this.reconnectAttempts.set(source, 0);
  }

  private emitConnectionStatus(source: 'mempool' | 'blockchain', status: 'connected' | 'disconnected' | 'error', message?: string): void {
    this.eventBus.emit({
      type: 'connection_status',
      timestamp: Date.now(),
      data: { source, status, message }
    });
  }

  private setupTestMode(): void {
    console.log('WebSocket Manager running in test mode');
    setTimeout(() => this.emitConnectionStatus('mempool', 'connected'), 100);
    setTimeout(() => this.emitConnectionStatus('blockchain', 'connected'), 150);
  }

  public simulateMessage(message: BlockchainWSMessage): void {
    // Allow simulation in any mode for testing
    this.handleBlockchainMessage(JSON.stringify(message));
  }

  disconnect(): void {
    this.heartbeatIntervals.forEach(interval => clearInterval(interval));
    this.heartbeatIntervals.clear();

    if (this.mempoolWS) {
      this.mempoolWS.close();
      this.mempoolWS = null;
    }

    if (this.blockchainWS) {
      this.blockchainWS.close();
      this.blockchainWS = null;
    }
  }
}